import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Button, Tag, Dropdown, Drawer, Grid } from "antd";
import {
  AuditOutlined,
  BankOutlined,
  DashboardOutlined,
  UserOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  LineChartOutlined,
  RobotOutlined,
  HeartOutlined,
  FlagOutlined,
  ApiOutlined,
  AppstoreOutlined,
  TagsOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  SettingOutlined,
  DatabaseOutlined,
  KeyOutlined,
  LogoutOutlined,
  HomeOutlined,
  MenuOutlined,
  ShopOutlined,
  TeamOutlined,
  UserAddOutlined,
  DollarOutlined,
  FileDoneOutlined,
  CalculatorOutlined,
  PercentageOutlined,
  ThunderboltOutlined,
  DollarCircleOutlined,
  SafetyOutlined,
  ProfileOutlined,
  MailOutlined,
  CloudUploadOutlined,
  CloudServerOutlined,
  LockOutlined,
  BookOutlined,
  WhatsAppOutlined,
  ClusterOutlined,
  OrderedListOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { AdminScopeProvider } from "../context/AdminScopeContext";
import { REVIEW_MODE_EVENT, isReviewModeEnabled } from "../utils/reviewMode";
import { fetchData } from "../api/apiService";
import { ADMIN } from "../api/endpointMap";
import ChangePasswordModal from "../components/ChangePasswordModal";

const { Sider, Content, Header } = Layout;
const { useBreakpoint } = Grid;

// Two top-level groups expose the same per-tenant operations under a
// scope-prefixed URL: Notice AI = direct customers (partner_id IS NULL),
// Partner Admin = partner-mediated tenants (partner_id IS NOT NULL). The
// remaining items live under the global section and operate platform-wide.
//
// Each child key here matches the `:section` segment under the prefix —
// `/admin/notice-ai/organizations` resolves children["organizations"]. The
// AdminScope context derives `direct` / `partner` from the prefix at runtime;
// no scope value travels through component props.
const SCOPED_SECTIONS = [
  // R17 — landing-style overview surface; counts the same 8 tables the
  // GST / IT / TDS-TCS service groups expose, but filtered to the
  // active scope (notice-ai = direct, partner = partner-mediated).
  { key: "portal-client", label: "Portal Client", icon: <AppstoreOutlined /> },
  { key: "organizations", label: "Organizations", icon: <BankOutlined /> },
  // Soft-deleted orgs view. Sits directly under Organizations because it's
  // the same data set in a different lifecycle state — admins managing one
  // expect the other one click away.
  { key: "deleted-organizations", label: "Deleted organizations", icon: <DeleteOutlined /> },
  { key: "users", label: "Users", icon: <UserOutlined /> },
  { key: "subscriptions", label: "Subscriptions", icon: <CreditCardOutlined /> },
  { key: "audit-logs", label: "Audit Logs", icon: <FileSearchOutlined /> },
  { key: "revenue", label: "Revenue", icon: <LineChartOutlined /> },
  { key: "ai-usage", label: "AI Usage", icon: <RobotOutlined /> },
  { key: "ops-health", label: "Ops Health", icon: <HeartOutlined /> },
];

// Global section — platform-wide management surfaces that aren't scoped to a
// single tenant set. Partners lives here because it manages partner records,
// not partner-mediated tenant data (per FE-1 spec out-of-scope note).
const GLOBAL_SECTIONS = [
  // Platform-wide Usage — same shape as the (now hidden) Partner
  // Admin Usage page but cross-tenant. Account / AI Usage /
  // Reliability cards + 2 trend charts.
  { key: "usage", label: "Usage", icon: <LineChartOutlined /> },
  // USAGE_TRACKING_PLAN Phase B — platform-wide billing rollup, no scope.
  { key: "billing", label: "Billing usage", icon: <DollarOutlined /> },
  // Slice 4 — cross-tenant Payment History / Orders.
  { key: "payments", label: "Payment History", icon: <DollarCircleOutlined /> },
  { key: "plans", label: "Plans", icon: <AppstoreOutlined /> },
  { key: "plan-categories", label: "Plan Categories", icon: <TagsOutlined /> },
  { key: "plan-features", label: "Plan Features", icon: <FlagOutlined /> },
  { key: "trial-config", label: "Trial Defaults", icon: <ClockCircleOutlined /> },
  { key: "system-config", label: "System Configuration", icon: <SettingOutlined /> },
  { key: "whatsapp-templates", label: "WhatsApp Templates", icon: <WhatsAppOutlined /> },
  // Celery ops (webhook-flood protection): fleet visibility + queued-task
  // view/stop. Two separate pages by design (user request 31-08-2026).
  { key: "celery-workers", label: "Workers", icon: <ClusterOutlined /> },
  { key: "celery-queue", label: "Queued Tasks", icon: <OrderedListOutlined /> },
  { key: "database-backups", label: "Database Backups", icon: <DatabaseOutlined /> },
  { key: "whitebooks-config", label: "Whitebooks (GST API)", icon: <CloudServerOutlined /> },
  { key: "cost-config", label: "Partner Pricing Management", icon: <DollarOutlined /> },
  { key: "bulk-ops", label: "Bulk Ops", icon: <ToolOutlined /> },
];

// Partner Data SubMenu — the three partner-related platform records, which
// were three loose rows at the top of the global section. They belong
// together: the partner record, the staff who sign in against it, and the
// intake queue that produces both.
//
// "Partner Staff" keeps its route key `partner-admin-users`: renaming the
// route would break existing links and bookmarks for a label change.
const PARTNER_DATA_GROUP = {
  key: "partner-data-group",
  label: "Partner Data",
  icon: <ApiOutlined />,
  children: [
    { key: "partners", label: "Partners", icon: <ApiOutlined /> },
    { key: "partner-admin-users", label: "Partner Staff", icon: <TeamOutlined /> },
    { key: "sandbox-access-requests", label: "Sandbox Access Requests", icon: <UserAddOutlined /> },
  ],
};

// Case Law Bank SubMenu — the two related surfaces (browse + settings)
// grouped under a single expandable parent. Same `children`-based shape
// as LOGS_GROUP + SERVICE_GROUPS so the sidebar renders it identically.
// Parent key is deliberately different from any child key so the Antd
// Menu keyspace stays clean; the routes themselves live at
// `/admin/case-bank` (list) + `/admin/case-bank/settings` (toggles).
const CASE_BANK_GROUP = {
  key: "case-bank-group",
  label: "Case Law Bank",
  icon: <BookOutlined />,
  children: [
    { key: "case-bank", label: "Documents", icon: <FileDoneOutlined /> },
    { key: "case-bank/settings", label: "Settings", icon: <SettingOutlined /> },
  ],
};

// Review Mode SubMenu — the managed-service surfaces grouped under one
// expandable parent. "Review Users" is the dedicated reviewer-account
// page (owns the Add Review User flow — deliberately NOT on the general
// Users page); "Review Dashboard" links OUT to the reviewer surface at
// /review-dashboard, which the BE also lets a super_admin view.
const REVIEW_MODE_GROUP = {
  key: "review-mode-group",
  label: "Review Mode",
  icon: <AuditOutlined />,
  children: [
    { key: "review-users", label: "Review Users", icon: <TeamOutlined /> },
    { key: "review-settings", label: "Review Settings", icon: <SettingOutlined /> },
    {
      key: "review-dashboard-link",
      label: "Review Dashboard",
      icon: <DashboardOutlined />,
      absolutePath: "/review-dashboard",
    },
  ],
};

// Logs SubMenu — the 5 admin log surfaces grouped under a single
// expandable parent so the global section stays compact. Matches the
// `children`-based shape of SERVICE_GROUPS so it renders the same way
// in the sidebar (parent row + indented leaves).
const LOGS_GROUP = {
  key: "logs",
  label: "Logs",
  icon: <ProfileOutlined />,
  children: [
    {
      key: "cost-event-logs",
      label: "Cost Event Log",
      icon: <DollarCircleOutlined />,
    },
    {
      key: "partner-billing-logs",
      label: "Partner Billing Log",
      icon: <DollarCircleOutlined />,
    },
    {
      key: "parallel-fetch-logs",
      label: "Parallel Fetch Log",
      icon: <ThunderboltOutlined />,
    },
    {
      key: "otps",
      label: "OTPs",
      icon: <SafetyOutlined />,
    },
    {
      key: "gst-auth-sessions",
      label: "GST Auth Sessions",
      icon: <LockOutlined />,
    },
    // {
    //   key: "email-logs",
    //   label: "Email Logs",
    //   icon: <MailOutlined />,
    // },
    {
      key: "generate-response-logs",
      label: "Generate Response Log",
      icon: <RobotOutlined />,
    },
    {
      key: "comprehensive-analysis-query-logs",
      label: "Comprehensive Analysis Log",
      icon: <FileSearchOutlined />,
    },
    {
      key: "import-logs",
      label: "Import Log",
      icon: <CloudUploadOutlined />,
    },
    {
      key: "login-failed-logs",
      label: "Login Failed Log",
      icon: <LockOutlined />,
    },
  ],
};

// Service-table sidebar groups — sit after `partner-admin-users` in the
// global section. Each parent SubMenu expands to leaf rows whose label
// renders a `(count)` badge fed by GET /admin/service-counts (single
// call, all 8 numbers). Counts default to "—" until the response lands;
// fetch failures degrade silently.
const SERVICE_GROUPS = [
  {
    key: "gst",
    label: "GST",
    icon: <FileDoneOutlined />,
    children: [
      { key: "gst/clients", label: "Clients", countPath: ["gst", "clients"] },
      {
        key: "gst/notices",
        label: "Notice & Order",
        countPath: ["gst", "notices"],
      },
      {
        key: "gst/additional-notices",
        label: "Additional Notice",
        countPath: ["gst", "additional_notices"],
      },
    ],
  },
  {
    key: "income-tax",
    label: "Income Tax",
    icon: <CalculatorOutlined />,
    children: [
      {
        key: "income-tax/clients",
        label: "Clients",
        countPath: ["income_tax", "clients"],
      },
      {
        key: "income-tax/eproceedings",
        label: "E-Proceeding",
        countPath: ["income_tax", "eproceedings"],
      },
      {
        key: "income-tax/response-outstanding-demands",
        label: "Response Outstanding Demand",
        countPath: ["income_tax", "response_outstanding_demands"],
      },
    ],
  },
  {
    key: "tds-tcs",
    label: "TDS/TCS",
    icon: <PercentageOutlined />,
    children: [
      {
        key: "tds-tcs/clients",
        label: "Clients",
        countPath: ["tds_tcs", "clients"],
      },
      {
        key: "tds-tcs/notices",
        label: "Notice",
        countPath: ["tds_tcs", "notices"],
      },
    ],
  },
];

const SUBMENU_OPEN_KEYS_STORAGE = "adminSidebar.openKeys";
// R15 — accordion mode: only one SubMenu open at a time. Default
// matches the index page (notice-ai/organizations).
const DEFAULT_OPEN_KEYS = ["notice-ai"];

const loadOpenKeys = () => {
  try {
    const raw = localStorage.getItem(SUBMENU_OPEN_KEYS_STORAGE);
    if (!raw) return DEFAULT_OPEN_KEYS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_OPEN_KEYS;
    // Trim legacy multi-open state down to one (latest by position).
    return parsed.slice(-1);
  } catch {
    return DEFAULT_OPEN_KEYS;
  }
};

// Maps a pathname to ([scope-key, section-key]) so the Menu can highlight the
// correct row + auto-open the parent SubMenu. `scope-key` is the SubMenu's
// own key ("notice-ai" / "partner") or "" for global rows; `section-key` is
// scope-prefixed when under a SubMenu so AntD treats sibling sections in
// different scopes as distinct selections.
const SERVICE_GROUP_KEYS = SERVICE_GROUPS.map((g) => g.key);
const LOGS_LEAF_KEYS = LOGS_GROUP.children.map((c) => c.key);

const resolveActiveKeys = (pathname) => {
  const parts = pathname.split("/").filter(Boolean); // ["admin", ...]
  if (parts[0] !== "admin") return { scopeKey: "", sectionKey: "organizations" };
  if (parts[1] === "notice-ai") {
    return {
      scopeKey: "notice-ai",
      sectionKey: `notice-ai:${parts[2] || "organizations"}`,
    };
  }
  if (parts[1] === "partner") {
    return {
      scopeKey: "partner",
      sectionKey: `partner:${parts[2] || "organizations"}`,
    };
  }
  if (parts[1] && SERVICE_GROUP_KEYS.includes(parts[1])) {
    // Service-group leaf rows use compound keys ("gst/clients" etc.) so
    // they don't collide with the scope-prefixed rows.
    return {
      scopeKey: parts[1],
      sectionKey: parts[2] ? `${parts[1]}/${parts[2]}` : parts[1],
    };
  }
  if (parts[1] && LOGS_LEAF_KEYS.includes(parts[1])) {
    // Logs SubMenu — leaf keys are plain ("cost-event-logs" etc.), the
    // parent SubMenu key is "logs" so it auto-opens when on a log page.
    return { scopeKey: "logs", sectionKey: parts[1] };
  }
  if (["partners", "partner-admin-users", "sandbox-access-requests"].includes(parts[1])) {
    // Partner Data submenu. A partner detail or edit route (/admin/partners/:id)
    // keeps the Partners leaf selected so the sidebar does not jump while an
    // admin drills into one.
    return { scopeKey: "partner-data-group", sectionKey: parts[1] };
  }
  if (parts[1] === "review-users") {
    // Review Mode submenu — the dedicated reviewer-accounts page.
    return { scopeKey: "review-mode-group", sectionKey: "review-users" };
  }
  if (parts[1] === "case-bank") {
    // Case Law Bank submenu — three FE routes share the "case-bank"
    // path prefix: list (`/admin/case-bank`), detail
    // (`/admin/case-bank/:tid`), settings (`/admin/case-bank/settings`).
    // Settings gets its own submenu-leaf highlight; list AND detail
    // both fall back to the Documents leaf so the sidebar stays
    // stable while an admin drills into a case.
    if (parts[2] === "settings") {
      return { scopeKey: "case-bank-group", sectionKey: "case-bank/settings" };
    }
    return { scopeKey: "case-bank-group", sectionKey: "case-bank" };
  }
  return { scopeKey: "", sectionKey: parts[1] || "organizations" };
};

// Walk a nested {a:{b:N}} object using ["a","b"] and return the leaf int
// if present, otherwise null. Used to feed sidebar count badges.
const pickCount = (obj, path) => {
  if (!obj) return null;
  let cur = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== "object") return null;
    cur = cur[key];
  }
  return typeof cur === "number" ? cur : null;
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  // Review Mode feature switch: the Review Mode group and the WhatsApp
  // Templates entry only render while the installation switch is ON.
  // Re-read when System Configuration saves the toggle (window event).
  const [reviewModeOn, setReviewModeOn] = useState(isReviewModeEnabled);
  useEffect(() => {
    const sync = () => setReviewModeOn(isReviewModeEnabled());
    window.addEventListener(REVIEW_MODE_EVENT, sync);
    return () => window.removeEventListener(REVIEW_MODE_EVENT, sync);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  // Tailwind-aligned breakpoints: mobile <768, tablet 768–1279, desktop ≥1280
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.xl;

  const [collapsed, setCollapsed] = useState(isTablet);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState(loadOpenKeys);
  // Sidebar count badges — fetched once on mount from the single
  // /admin/service-counts endpoint. Failure leaves counts null and the
  // labels render "—" instead.
  const [serviceCounts, setServiceCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchData(ADMIN.SERVICE_COUNTS)
      .then((raw) => {
        if (cancelled) return;
        setServiceCounts(raw?.result ?? raw ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Surface in console so a 404 (BE not reloaded) / 401 / 500 is
        // visible to admins debugging missing badges. Layout still renders.
        // eslint-disable-next-line no-console
        console.warn("[admin sidebar] service-counts fetch failed:", err);
        setServiceCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep sidebar collapsed state in sync with viewport changes.
  useEffect(() => {
    if (isTablet) setCollapsed(true);
    else if (!isMobile) setCollapsed(false);
  }, [isTablet, isMobile]);

  // Close mobile drawer on route change so users aren't left with it open.
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  // R15 — accordion mode: only one SubMenu open at a time. On path
  // change we collapse all other groups and keep just the active
  // scope's parent open. On user click (handled by `persistOpenKeys`)
  // we keep only the latest-opened key, closing siblings.
  useEffect(() => {
    const { scopeKey } = resolveActiveKeys(location.pathname);
    if (!scopeKey) return;
    setOpenKeys((prev) => {
      if (prev.length === 1 && prev[0] === scopeKey) return prev;
      const next = [scopeKey];
      try {
        localStorage.setItem(SUBMENU_OPEN_KEYS_STORAGE, JSON.stringify(next));
      } catch {
        // localStorage may be unavailable in private-browsing — ignore.
      }
      return next;
    });
  }, [location.pathname]);

  // Accordion: when AntD Menu reports a new openKeys array, find the
  // latest-opened key (one that wasn't in the previous state). If the
  // user opened something new, keep ONLY that key (close siblings).
  // If they only closed a key (no new opens), accept whatever's left.
  const persistOpenKeys = (keys) => {
    setOpenKeys((prev) => {
      const latestOpened = keys.find((k) => !prev.includes(k));
      const next = latestOpened ? [latestOpened] : keys;
      try {
        localStorage.setItem(SUBMENU_OPEN_KEYS_STORAGE, JSON.stringify(next));
      } catch {
        // localStorage may be unavailable in private-browsing — ignore.
      }
      return next;
    });
  };

  const buildScopedChildren = (prefix) =>
    SCOPED_SECTIONS.map((section) => ({
      key: `${prefix}:${section.key}`,
      icon: section.icon,
      label: (
        <NavLink to={`/admin/${prefix}/${section.key}`}>
          {section.label}
        </NavLink>
      ),
    }));

  const buildServiceGroups = () =>
    SERVICE_GROUPS.map((group) => ({
      key: group.key,
      icon: group.icon,
      label: group.label,
      children: group.children.map((leaf) => {
        const count = pickCount(serviceCounts, leaf.countPath);
        return {
          key: leaf.key,
          label: (
            <NavLink to={`/admin/${leaf.key}`}>
              {leaf.label}
              <span className="text-slate-400 ml-1 text-xs">
                ({count ?? "-"})
              </span>
            </NavLink>
          ),
        };
      }),
    }));

  // Insert service groups directly after `partner-admin-users` in the
  // global section so they sit visually right under "Partner Staff".
  const buildGlobalRows = () => {
    const items = [];
    // Leads the global section, where the three loose partner rows were.
    items.push({
      key: PARTNER_DATA_GROUP.key,
      icon: PARTNER_DATA_GROUP.icon,
      label: PARTNER_DATA_GROUP.label,
      children: PARTNER_DATA_GROUP.children.map((c) => ({
        key: c.key,
        icon: c.icon,
        label: <NavLink to={`/admin/${c.key}`}>{c.label}</NavLink>,
      })),
    });
    GLOBAL_SECTIONS.forEach((section) => {
      if (section.key === "whatsapp-templates" && !reviewModeOn) return;
      items.push({
        key: section.key,
        icon: section.icon,
        label: (
          <NavLink to={`/admin/${section.key}`}>{section.label}</NavLink>
        ),
      });
      if (section.key === "partner-admin-users") {
        // Commented out per Hemant sir's request — GST / Income Tax /
        // TDS-TCS SubMenu groups hidden from the sidebar. The Portal
        // Client entry (under Notice AI / Partner Admin scopes) is
        // the primary entry point now; routes still resolve so direct
        // URLs keep working.
        // items.push(...buildServiceGroups());
      }
    });
    // Case Law Bank SubMenu — Documents (list) + Settings, grouped so
    // both related surfaces share one visual row. Sits above Logs
    // because it's platform-config-ish, not per-run diagnostic.
    items.push({
      key: CASE_BANK_GROUP.key,
      icon: CASE_BANK_GROUP.icon,
      label: CASE_BANK_GROUP.label,
      children: CASE_BANK_GROUP.children.map((leaf) => ({
        key: leaf.key,
        icon: leaf.icon,
        label: (
          <NavLink to={`/admin/${leaf.key}`}>{leaf.label}</NavLink>
        ),
      })),
    });
    // Review Mode SubMenu — reviewer accounts + a jump-out link to the
    // reviewer-facing dashboard (absolutePath leaves the /admin prefix).
    // Only while the Review Mode switch is ON.
    if (reviewModeOn) items.push({
      key: REVIEW_MODE_GROUP.key,
      icon: REVIEW_MODE_GROUP.icon,
      label: REVIEW_MODE_GROUP.label,
      children: REVIEW_MODE_GROUP.children.map((leaf) => ({
        key: leaf.key,
        icon: leaf.icon,
        label: (
          <NavLink to={leaf.absolutePath || `/admin/${leaf.key}`}>
            {leaf.label}
          </NavLink>
        ),
      })),
    });
    // Logs SubMenu — the 5 admin log surfaces grouped under one
    // expandable parent (matches SERVICE_GROUPS children-based shape).
    items.push({
      key: LOGS_GROUP.key,
      icon: LOGS_GROUP.icon,
      label: LOGS_GROUP.label,
      children: LOGS_GROUP.children.map((leaf) => ({
        key: leaf.key,
        icon: leaf.icon,
        label: (
          <NavLink to={`/admin/${leaf.key}`}>{leaf.label}</NavLink>
        ),
      })),
    });
    return items;
  };

  const buildMenuItems = () => [
    {
      key: "notice-ai",
      icon: <ShopOutlined />,
      label: "Notice AI",
      children: buildScopedChildren("notice-ai"),
    },
    {
      key: "partner",
      icon: <TeamOutlined />,
      label: "Partner Admin",
      children: buildScopedChildren("partner"),
    },
    { type: "divider" },
    ...buildGlobalRows(),
  ];

  const { sectionKey } = resolveActiveKeys(location.pathname);
  const selectedKeys = [sectionKey];

  const userMenu = {
    items: [
      {
        key: "tenant",
        icon: <HomeOutlined />,
        label: "Back to tenant app",
        onClick: () => navigate("/"),
      },
      {
        // Self-service credential rotation — the flow the user-detail
        // page's disabled "Set Password" tooltip points at for one's own
        // account (the direct-set endpoint rejects self-targets so a
        // change always proves the current password).
        key: "change-password",
        icon: <KeyOutlined />,
        label: "Change password",
        onClick: () => setChangePasswordOpen(true),
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Log out",
        danger: true,
        onClick: logout,
      },
    ],
  };

  const SidebarBrand = ({ short }) => (
    <div className="h-16 flex items-center justify-center border-b border-slate-200">
      <span className="font-semibold text-slate-800 tracking-wide">
        {short ? "NA" : "NoticeAI Admin"}
      </span>
    </div>
  );

  return (
    <AdminScopeProvider>
      {/* h-screen (not min-h-screen) pins the outer layout to viewport
          height so the Sider can't scroll away with the page. The inner
          right-hand Layout owns the only scroll container (the Content),
          giving us a frozen sidebar + frozen header + scrollable body. */}
      <Layout className="h-screen overflow-hidden">
        {/* Desktop + tablet: persistent sidebar */}
        {!isMobile && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            theme="light"
            width={240}
            collapsedWidth={72}
            className="border-r border-slate-200 h-screen overflow-y-auto"
          >
            <SidebarBrand short={collapsed} />
            <Menu
              mode="inline"
              selectable
              selectedKeys={selectedKeys}
              openKeys={collapsed ? [] : openKeys}
              onOpenChange={persistOpenKeys}
              items={buildMenuItems()}
              className="border-r-0"
              style={{ paddingBottom: 56 }}
            />
          </Sider>
        )}

        {/* Mobile: drawer-based sidebar triggered by a hamburger in the header */}
        {isMobile && (
          <Drawer
            placement="left"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            width={260}
            styles={{ body: { padding: 0 } }}
            title="NoticeAI Admin"
          >
            <Menu
              mode="inline"
              selectable
              selectedKeys={selectedKeys}
              openKeys={openKeys}
              onOpenChange={persistOpenKeys}
              items={buildMenuItems()}
              className="border-r-0"
            />
          </Drawer>
        )}

        <Layout className="h-screen overflow-hidden">
          <Header className="bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 flex-none">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {isMobile && (
                <Button
                  type="text"
                  icon={<MenuOutlined />}
                  onClick={() => setMobileDrawerOpen(true)}
                  aria-label="Open navigation"
                />
              )}
              <Tag color="geekblue" className="m-0">
                ADMIN
              </Tag>
              <span className="text-slate-500 text-sm hidden md:inline truncate">
                Platform Operations - Super Admin Only
              </span>
            </div>
            <Dropdown menu={userMenu} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />}>
                <span className="hidden sm:inline">
                  {user?.full_name || user?.email || "Admin"}
                </span>
              </Button>
            </Dropdown>
          </Header>
          {/* The single scroll surface for the whole admin app. Sider +
              Header stay frozen; only this Content scrolls when a page
              (Cost Config, Notices, etc.) overflows the viewport. */}
          <Content className="p-4 sm:p-6 bg-slate-50 flex-auto overflow-y-auto">
            <Outlet />
          </Content>
        </Layout>

        <ChangePasswordModal
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />
      </Layout>
    </AdminScopeProvider>
  );
};

export default AdminLayout;
